import { brand } from "../../config/theme";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { CLIENT_VIEWS, clientViewRoute } from "./config";

// Shell de la vista del cliente (CC-CONSOLE #981): Header + sub-menú con las
// tres apps (Horas, Equipo, Gastos) + menú de apps + la sección activa. Mismo
// patrón que StaffLayout.
export default function ClientViewLayout() {
  const { t } = useTranslation("common");
  const { themeMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const active = CLIENT_VIEWS.find((v) => location.pathname.startsWith(v.base)) ?? CLIENT_VIEWS[0];

  const handleAppSelect = (app: AppItem) => {
    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.route) {
      setIsAppsSidebarOpen(false);
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className="h-full bg-app">
      {isExpiringSoon && (
        <div className="flex items-center justify-center gap-2 bg-amber-500 px-4 py-2 text-center text-sm text-white">
          <i className="ri-alarm-warning-line"></i>
          <span>{t("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header title={t("clientView.header")} subtitle={t(active.labelKey)} onOpenAppsSidebar={() => setIsAppsSidebarOpen(true)} isAppsSidebarOpen={isAppsSidebarOpen} />

      <div className="flex h-full">
        <AppSidebarMenu
          menuItems={CLIENT_VIEWS.map((v) => ({ id: v.appId, label: t(v.labelKey), icon: v.iconClass, path: clientViewRoute(v) }))}
          isMobileMenuOpen={false}
          onCloseMobileMenu={() => {}}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) => itemId === active.appId}
          footerStats={{
            label: t("clientView.header"),
            items: [{ label: t("clientView.readOnly"), value: CLIENT_VIEWS.length, className: "text-primary-500" }],
          }}
        />

        <div className="flex w-full flex-1 justify-center overflow-hidden">
          <Outlet />
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={() => setIsAppsSidebarOpen(false)}
        categories={filteredCategories}
        selectedAppId={active.appId}
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={user ? { first_name: user.first_name, last_name: user.last_name, email: user.email, username: user.username } : undefined}
        onProfileClick={() => {
          setIsAppsSidebarOpen(false);
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
